<template>
    <div class="p-8 pt-0">
        <vs-tabs>
            <vs-tab :label="$t('admin.resorts.tabs.general')">
                <div class="flex flex-col gap-4 mt-4 w-2/3 md:w-2/3 lg:w-1/2 xl:w-1/3">
                    <div>
                        <div class="flex flex-col vx-card p-8">
                            <div>
                                <LabelForm font-size="text-sm" margins="mt-0">{{ $t('admin.communities.name') }}</LabelForm>
                                <vs-input class="border-grey-light w-full"
                                          type="text"
                                          v-model="resort.name"
                                          :danger="resort.name == ''"
                                />
                            </div>

                            <div>
                                <vs-checkbox class="ml-0 mb-4 mt-4 text-dark" v-model="resort.is_ski_resort">
                                    {{ $t('admin.resorts.is_ski_resort') }}
                                </vs-checkbox>
                            </div>
                        </div>
                    </div>

                    <div class="mt-8">
                        <h3 class="font-semibold text-base mb-4">{{ $t('apps.configurator.manage-item.configuration') }}</h3>
                        <div class="flex flex-col vx-card p-8">
                            <div>
                                <LabelForm font-size="text-sm" margins="mt-0">{{ $t('timeofyear') }}</LabelForm>
                                <Combobox
                                    v-model="resort.timeofyear"
                                    class="mt-2"
                                    :options="['winter', 'summer', 'always'].map(timeofyear => ({key: timeofyear, text: this.$t(`admin.interactive-maps.timeofyear-${timeofyear}`)}))"
                                />
                            </div>

                            <div>
                                <LabelForm font-size="text-sm">{{ $t('admin.apps.main.country') }}</LabelForm>
                                <Combobox
                                    v-model="resort.country_id"
                                    class="mt-2"
                                    :options="countries.map(country => ({key: parseInt(country.id), text: country.name}))"
                                />
                            </div>

                            <div>
                                <LabelForm font-size="text-sm">{{ $t('admin.challenges.settings.timezone') }}</LabelForm>
                                <Combobox
                                    v-model="resort.timezone"
                                    class="mt-2"
                                    :options="timezones.map(timezone => ({key: timezone, text: timezone}))"
                                />
                            </div>

                            <div>
                                <LabelForm font-size="text-sm">{{ $t('admin.resorts.reference_system') }}</LabelForm>
                                <Combobox
                                    v-model="resort.reference_system"
                                    class="mt-2"
                                    :options="['metric', 'imperial'].map(referenceSystem => ({key: referenceSystem, text: referenceSystem}))"
                                />
                            </div>

                            <div>
                                <LabelForm font-size="text-sm">{{ $t('admin.resorts.facilities_difficulty_style') }}</LabelForm>
                                <Combobox
                                    v-model="resort.facilitiesdifficultystyle_id"
                                    class="mt-2"
                                    :options="getFacilityDifficultyStyles().map(style => ({key: style.id, text: style.name}))"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="mt-8">
                        <h3 class="font-semibold text-base mb-4">{{ $t('admin.resorts.data_providers') }}</h3>
                        <div class="flex flex-col vx-card p-8">
                            <div>
                                <LabelForm font-size="text-sm" margins="mt-0">{{ $t('admin.resorts.snowreport_provider') }}</LabelForm>
                                <Combobox
                                    v-model="resort.dataprovider_id"
                                    class="mt-2"
                                    :options="[{ id: null, name: this.$t('admin.apps.main.none')}, ...snowreportProviders].map(provider => ({key: provider.id, text: provider.name}))"
                                />
                            </div>

                            <div>
                                <LabelForm font-size="text-sm">{{ $t('admin.resorts.weather_provider') }}</LabelForm>
                                <Combobox
                                    v-model="resort.weatherdataprovider_id"
                                    class="mt-2"
                                    :options="[{ id: null, name: this.$t('admin.apps.main.none')}, ...weatherProviders].map(provider => ({key: provider.id, text: provider.name}))"
                                />
                            </div>

                            <div>
                                <LabelForm font-size="text-sm">{{ $t('admin.resorts.touristic_objects_provider') }}</LabelForm>
                                <Combobox
                                    v-model="resort.touristic_object_data_provider_id"
                                    class="mt-2"
                                    :options="[{ id: null, name: this.$t('admin.apps.main.none')}, ...touristicObjectProviders].map(provider => ({key: provider.id, text: provider.name}))"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="mt-8">
                        <h3 class="font-semibold text-base mb-4">{{ $t('apps.configurator.resources.title') }}</h3>
                        <div class="flex flex-col vx-card p-8">
                            <div>
                                <LabelForm font-size="text-sm" margins="mt-0">{{ $t('profile.label.avatar') }}</LabelForm>
                                <ImageForm
                                    @updateImage="handleUpdateAvatar"
                                    class="w-full mt-4"
                                    id="avatar"
                                    :valid-sizes="[{ width: '250', height: '250' }]"
                                    validationWeight="500"
                                    :validationMessage="this.$t('apps.campaigns.section_content_validation_image_error')"
                                    constraintsText="Png 250x250px (max. 500Kb)"
                                    height="250"
                                    :src="resort.avatar"
                                />
                            </div>

                            <div>
                                <LabelForm font-size="text-sm">{{ $t('profile.label.avatar') + ' 322x250' }}</LabelForm>
                                <ImageForm
                                    @updateImage="handleUpdateAvatar322x250"
                                    class="w-full mt-4"
                                    id="avatar"
                                    :valid-sizes="[{ width: '322', height: '250' }]"
                                    validationWeight="500"
                                    :validationMessage="this.$t('apps.campaigns.section_content_validation_image_error')"
                                    constraintsText="Png 322x250px (max. 500Kb)"
                                    height="250"
                                    :src="resort.avatar_322x250"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="mt-8">
                        <h3 class="font-semibold text-base mb-4">{{ $t('admin.resorts.location') }}</h3>
                        <div class="flex flex-col vx-card p-8">
                            <div>
                                <MapCoordinates
                                    :defaultCoordinates="{ lat: resort.lat, lon: resort.lng }"
                                    :componentSize="{ width: 'w-full' }"
                                    :zoom=5
                                    @updated="handleMapChange"
                                    @invalidCoords="handleInvalidCoords"
                                />

                                <p class="mt-8 mb-0">{{ $t('admin.resorts.altitude') }}</p>
                                <vs-input class="border-grey-light w-full mt-0"
                                          type="number"
                                          v-model="resort.lat_lng_height"
                                          :danger="resort.lat_lng_height == ''"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="mt-8">
                        <div class="flex items-center ml-0 gap-4">
                            <vs-button :disabled="!isValid" class="rounded-lg xs:w-full sm:w-auto" @click="save">
                                {{ $t('resorts.touristic-objects.form.saveButton') }}
                            </vs-button>
                            <vs-button type="border" class="rounded-lg xs:w-full sm:w-auto text-primary" @click="$router.push({'name': 'admin-resorts'})">
                                {{ $t('resorts.touristic-objects.form.cancelButton') }}
                            </vs-button>
                        </div>
                    </div>
                </div>
            </vs-tab>

            <vs-tab :label="$t('admin.resorts.tabs.integrations')">
                <ResortIntegrations :resort-uuid="resort.uuid" />
            </vs-tab>
        </vs-tabs>
    </div>
</template>

<script>

import store from "@/modules/Shared/Store/store";
import loader from "@/modules/Shared/Mixins/loader";
import LabelForm from "@/modules/Shared/Components/form/LabelForm.vue";
import ResortService from "@/modules/Admin/Services/ResortService";
import Combobox from "@/modules/Shared/Components/Combobox.vue";
import {unifyPaginatedResponse} from "@/modules/Shared/Helpers/apiResponseHelper";
import CountryService from "@/modules/Shared/Services/CountryService";
import timezones from '@/timezones.json'
import MapCoordinates from "@/modules/Shared/Components/mapcoordinates/MapCoordinates.vue";
import {getFacilityDifficultyStyles} from "@/modules/Resorts/Helpers/facilityHelper";
import ImageForm from "@/modules/Shared/Components/form/ImageForm.vue";
import AssetsService from "@/modules/Shared/Services/AssetsService";
import ResortIntegrations from "@/modules/Admin/Components/Integrations/ResortIntegrations.vue";

export default {
    name: "EditResortPage",
    components: {ImageForm, MapCoordinates, Combobox, LabelForm, ResortIntegrations},
    mixins: [loader],
    data() {
        return {
            templatesComboboxPage: 1,
            templatesComboboxEnded: false,
            countries: [],
            timezones: [],
            snowreportProviders: [],
            weatherProviders: [],
            touristicObjectProviders: [],
            resort: null,
            new: true,
            uploadedAvatarFilename: null,
            uploadedAvatar322x250Filename: null
        }
    },

    computed: {
        isValid() {
            return this.resort.name &&
                this.resort.country_id &&
                this.resort.timeofyear &&
                this.resort.timezone &&
                this.resort.reference_system &&
                this.resort.lat &&
                this.resort.lng
        }
    },

    async created() {
        await this.load(async () => {
            this.timezones = await timezones
            const [countries, snowreportProviders, weatherProviders, touristicObjectProviders] = await Promise.all([
                unifyPaginatedResponse(CountryService.getCountries, {}),
                unifyPaginatedResponse(ResortService.getSnowreportProviders, {q: 'published:1'}),
                unifyPaginatedResponse(ResortService.getWeatherProviders, {q: 'published:1'}),
                unifyPaginatedResponse(ResortService.getTouristicObjectProviders, {q: 'published:1'}),
            ])

            this.countries = countries
            this.snowreportProviders = snowreportProviders
            this.weatherProviders = weatherProviders
            this.touristicObjectProviders = touristicObjectProviders
        })
    },

    async beforeRouteEnter(to, from, next) {
        try {
            const resort = await ResortService.getResort(to.params.uuid);
            await store.dispatch('setRouteElement', resort)
            next(vm => {
                vm.resort = resort
                vm.new = false
            })
        } catch (error) {
            await store.dispatch('setRouteElement', null)
            next(vm => {
                vm.resort = {
                    uuid: to.params.uuid,
                    name: "",
                    is_ski_resort: true,
                    country_id: 1,
                    timezone: "Europe/Madrid",
                    timeofyear: "always",
                    reference_system: "metric",
                    facilitiesdifficultystyle_id: 2,
                    dataprovider_id: null,
                    weatherdataprovider_id: null,
                    touristic_object_data_provider_id: null,
                    lat: 0,
                    lng: 0,
                    lat_lng_height: null,
                    avatar: null,
                    avatar_322x250: null
                }
            })
        }
    },
    methods: {
        getFacilityDifficultyStyles,
        handleMapChange(eventDataNewCoordinates) {
            this.resort.lat = eventDataNewCoordinates.lat
            this.resort.lng = eventDataNewCoordinates.lon
        },
        handleInvalidCoords(eventInvalidCoordinates) {
            this.$vs.notify({
                type: 'confirm',
                title: "Error",
                text: `${eventInvalidCoordinates}`,
                color: "danger",
                position: "top-right"
            });
        },
        async uploadAvatar(file) {
            const binaryAsset = new Blob([file]);
            const name = `${Date.now()}__${file.name}`

            const presignedUrlResponse = await AssetsService.getTempAssetResource(name)
            await AssetsService.uploadFileInS3Resource(presignedUrlResponse.url, binaryAsset)
        },
        async handleUpdateAvatar(file) {
            if (file) {
                await this.uploadAvatar(file)

                this.uploadedAvatarFilename = name
                this.resort.avatar = URL.createObjectURL(file)
            } else {
                this.uploadedAvatarFilename = null
                this.resort.avatar = null
            }
        },
        async handleUpdateAvatar322x250(file) {
            if (file) {
                await this.uploadAvatar(file)

                this.uploadedAvatar322x250Filename = name
                this.resort.avatar_322x250 = URL.createObjectURL(file)
            } else {
                this.uploadedAvatar322x250Filename = null
                this.resort.avatar_322x250 = null
            }
        },
        save() {
            this.loadAndNotify(async () => {
                const payload = {
                    ...this.resort,
                    avatar: this.uploadedAvatarFilename,
                    avatar_322x250: this.uploadedAvatar322x250Filename
                }

                if (this.new) {
                    await ResortService.createResort(this.$route.params.uuid, payload);
                } else {
                    await ResortService.updateResort(this.resort.uuid, payload);
                }

                await this.$router.push({'name': 'admin-resorts'});
            })
        },
    }
};
</script>
